import type {
  ConsoleModel,
  ConsoleOperation,
  ConsoleParameter,
  ConsoleTagGroup,
  HttpMethod,
  OpenApiParameter,
  ParsedOpenApi,
  Swagger2Document,
} from './types';

const HTTP_METHODS: HttpMethod[] = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

const TAG_LABELS: Record<string, string> = {
  '1_gda': 'GENE-DISEASE',
  '2_vda': 'VARIANT-DISEASE',
  '3_dda': 'DISEASE-DISEASE',
  '4_entity': 'ENTITIES',
  '5_enrichment': 'ENRICHMENT',
  '6_embeddings': 'ONTOLOGY',
  '7_gsea': 'GSEA',
  '7_historical': 'HISTORICAL',
  '8_historical': 'HISTORICAL',
  '8_management': 'MANAGEMENT',
  '9_management': 'MANAGEMENT',
};

/** Sensible DISGENET examples used when the OpenAPI spec omits `example`/`default`. */
const PARAM_EXAMPLES: Record<string, string> = {
  gene_symbol: 'APOE',
  gene_ncbi_id: '348',
  gene_ensembl_id: 'ENSG00000130203',
  uniprot_id: 'P02649',
  disease: 'UMLS_C0002395',
  disease_1: 'UMLS_C0002395',
  disease_2: 'UMLS_C0020538',
  chemical_id: 'CHEMBL_CHEMBL4594216',
  variant_id: 'rs429358',
  min_score: '0.5',
  max_score: '1',
  page_number: '0',
  page_size: '10',
  source: 'CURATED',
  order_by: 'score',
  order: 'DESC',
  format: 'json',
};

/** Full example presets for specific endpoints (Load examples / initial selection). */
const OPERATION_PARAM_PRESETS: Record<string, Record<string, string>> = {
  '/api/v1/gda/summary': {
    gene_ncbi_id: '348',
    gene_symbol: 'APOE',
    uniprot_id: 'P02649',
    disease: 'UMLS_C2673196',
    source: 'CURATED',
    disease_prevalence_class: '<1 / 1 000 000',
    disease_inheritance: 'Autosomal dominant',
    order_by: 'score',
    order: 'DESC',
    page_number: '1',
  },
};

export function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/pre>/gi, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function decodeBasicEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

/** Extract text inside <pre> blocks (used for GSEA ranking examples, etc.). */
export function extractPreformattedExamples(html: string): string[] {
  if (!html) return [];
  const matches = [...html.matchAll(/<pre[^>]*>([\s\S]*?)<\/pre>/gi)];
  return matches.map(match => decodeBasicEntities(match[1]).replace(/^\n+|\n+$/g, '')).filter(Boolean);
}

/** Keep a small HTML allowlist so OpenAPI descriptions keep structure (lists, bold, links, pre). */
export function sanitizeDescriptionHtml(html: string): string {
  if (!html) return '';

  // Protect <pre> contents so inner whitespace/tabs survive tag filtering.
  const preBlocks: string[] = [];
  let out = html.replace(/<pre[^>]*>[\s\S]*?<\/pre>/gi, match => {
    const inner = match.replace(/^<pre[^>]*>/i, '').replace(/<\/pre>$/i, '');
    const idx = preBlocks.length;
    preBlocks.push(decodeBasicEntities(inner));
    return `%%PRE_BLOCK_${idx}%%`;
  });

  out = out.replace(/&nbsp;/gi, ' ').replace(/<\/?(script|style|iframe|object|embed)[^>]*>/gi, '');

  out = out.replace(/<\/?([a-z0-9]+)(\s[^>]*)?>/gi, (full, tag: string, attrs = '') => {
    const t = tag.toLowerCase();
    const allowed = new Set([
      'br',
      'b',
      'strong',
      'i',
      'em',
      'u',
      'p',
      'ul',
      'ol',
      'li',
      'code',
      'a',
      'span',
      'pre',
      'h1',
      'h2',
      'h3',
      'h4',
    ]);
    if (!allowed.has(t)) return '';
    if (t === 'br') return '<br />';
    if (full.startsWith('</')) return `</${t}>`;
    if (t === 'a') {
      const href = attrs.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)')/i);
      const url = href?.[2] ?? href?.[3] ?? '';
      if (!/^https?:\/\//i.test(url)) return '<span>';
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">`;
    }
    return `<${t}>`;
  });

  out = out.replace(/%%PRE_BLOCK_(\d+)%%/g, (_, index) => {
    const content = preBlocks[Number(index)] ?? '';
    return `<pre>${content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
  });

  return out;
}

function shortPath(path: string): string {
  return path.replace(/^\/api\/v\d+/, '') || path;
}

function resolveTagLabel(name: string, description?: string): string {
  if (TAG_LABELS[name]) return TAG_LABELS[name];
  if (description) {
    return description
      .replace(/DISGENET\s*/i, '')
      .replace(/\s+associations?/i, '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
  return name.replace(/^\d+_/, '').toUpperCase();
}

export function extractDefaultFromDescription(description: string): string | undefined {
  const match = description.match(/default value(?:\s+is)?\s*[:=]?\s*([A-Za-z0-9_.\-]+)/i);
  const value = match?.[1];
  if (!value) return undefined;
  return value.replace(/\.+$/, '') || undefined;
}

export function extractExampleFromDescription(description: string): string | undefined {
  const patterns = [
    /valid[^.]*?\bcould be\s+([A-Za-z0-9_:\.\-]+)/i,
    /for instance[^.]*?\b([A-Z]{2,}[_:][A-Za-z0-9_:\.\-]+)/i,
    /e\.g\.\s*:?\s*([A-Za-z0-9_:\.\-]+)/i,
    /example[s]?\s*[:=]\s*([A-Za-z0-9_:\.\-]+)/i,
  ];
  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match?.[1]) return match[1];
  }
  return undefined;
}

function resolveExample(param: OpenApiParameter, description: string): string | undefined {
  const fromSpec =
    (param as { example?: string | number | boolean }).example ??
    (param as { 'x-example'?: string | number | boolean })['x-example'];
  if (fromSpec != null && fromSpec !== '') return String(fromSpec);

  const fromDesc = extractExampleFromDescription(description);
  if (fromDesc) return fromDesc;

  if (PARAM_EXAMPLES[param.name] != null) return PARAM_EXAMPLES[param.name];

  const enumValues = param.enum ?? param.items?.enum;
  if (enumValues?.length) return String(enumValues[0]);

  return undefined;
}

function normalizeParameter(param: OpenApiParameter): ConsoleParameter {
  const schema = (param.schema ?? {}) as SchemaNode;
  const enumValues =
    param.enum ??
    param.items?.enum ??
    (schema.enum as string[] | undefined) ??
    ((schema.items as SchemaNode | undefined)?.enum as string[] | undefined);
  const isArray = param.type === 'array' || schema.type === 'array';
  const isFile = param.type === 'file' || schema.type === 'file';
  const baseType = param.type ?? (schema.type as string | undefined) ?? (param.schema ? 'object' : 'string');
  const type = isFile
    ? 'file'
    : isArray && enumValues
      ? 'enum'
      : enumValues
        ? 'enum'
        : isArray
          ? `array<${param.items?.type ?? (schema.items as SchemaNode | undefined)?.type ?? 'string'}>`
          : baseType;

  const descriptionHtml = sanitizeDescriptionHtml(param.description ?? '');
  const description = stripHtml(param.description ?? '');
  const defaultFromDesc = extractDefaultFromDescription(description);
  const defaultValue =
    param.default != null
      ? String(param.default)
      : schema.default != null
        ? String(schema.default)
        : defaultFromDesc;
  const example =
    resolveExample(param, description) ?? (schema.example != null ? String(schema.example) : undefined);

  return {
    name: param.name,
    in: param.in,
    description,
    descriptionHtml,
    required: Boolean(param.required),
    type,
    enumValues,
    multi: isArray,
    defaultValue,
    example,
  };
}

function resolveRefName(ref: string): string {
  return ref.replace(/^#\/definitions\//, '');
}

type SchemaNode = Record<string, unknown>;

/**
 * Build a request/response example object from a Swagger 2 schema + definitions,
 * preferring property-level `example` values (as Swagger UI does).
 */
export function buildExampleFromSchema(
  schema: SchemaNode | undefined,
  definitions: Record<string, SchemaNode> = {},
  seen: Set<string> = new Set(),
): unknown {
  if (!schema) return {};

  if (typeof schema.$ref === 'string') {
    const name = resolveRefName(schema.$ref);
    if (seen.has(name)) return {};
    seen.add(name);
    return buildExampleFromSchema(definitions[name], definitions, seen);
  }

  if (schema.example != null) return schema.example;

  const type = schema.type as string | undefined;

  if (type === 'object' || schema.properties) {
    const props = (schema.properties ?? {}) as Record<string, SchemaNode>;
    const out: Record<string, unknown> = {};
    for (const [key, propSchema] of Object.entries(props)) {
      out[key] = buildExampleFromSchema(propSchema, definitions, seen);
    }
    return out;
  }

  if (type === 'array') {
    const items = schema.items as SchemaNode | undefined;
    return [buildExampleFromSchema(items, definitions, seen)];
  }

  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];
  if (type === 'integer' || type === 'number') return 0;
  if (type === 'boolean') return false;
  if (type === 'string') return '';
  return null;
}

export function stringifyExample(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

function operationRequiresAuth(op: { security?: Array<Record<string, string[]>> }): boolean {
  if (!op.security) return false;
  return op.security.some(entry => Object.keys(entry).length > 0);
}

function buildBaseUrl(doc: Swagger2Document, fallbackBase: string): string {
  const envBase = fallbackBase.replace(/\/$/, '');
  if (envBase) return envBase;

  const scheme = doc.schemes?.[0] ?? 'https';
  const host = doc.host ?? 'api.disgenet.com';
  const basePath = (doc.basePath ?? '/').replace(/\/$/, '');
  return `${scheme}://${host}${basePath === '/' ? '' : basePath}`;
}

export function parseSwagger2Document(
  doc: Swagger2Document,
  fallbackBase = process.env.REACT_APP_URL_API_API ?? '',
): ParsedOpenApi {
  const tagMeta = new Map((doc.tags ?? []).map(t => [t.name, t]));
  const operations: ConsoleOperation[] = [];

  for (const [path, pathItem] of Object.entries(doc.paths ?? {})) {
    if (!pathItem) continue;
    for (const method of HTTP_METHODS) {
      const raw = pathItem[method];
      if (!raw) continue;

      const tag = raw.tags?.[0] ?? 'other';
      const tagInfo = tagMeta.get(tag);
      const parameters = (raw.parameters ?? []).filter(p => p.in !== 'body').map(normalizeParameter);

      const bodyParam = (raw.parameters ?? []).find(p => p.in === 'body');
      if (bodyParam) {
        const descriptionHtml = sanitizeDescriptionHtml(bodyParam.description ?? 'Request body');
        const schema = bodyParam.schema as SchemaNode | undefined;
        const preExample = extractPreformattedExamples(raw.description ?? '')[0];
        const schemaExample = buildExampleFromSchema(schema, doc.definitions as Record<string, SchemaNode>);
        const isPlainStringBody = schema?.type === 'string' && !schema?.properties && !schema?.$ref;
        const exampleValue = isPlainStringBody
          ? preExample ||
            (typeof schema?.example === 'string' ? schema.example : '') ||
            (typeof schemaExample === 'string' ? schemaExample : '')
          : schemaExample;
        const exampleText = isPlainStringBody
          ? String(exampleValue || preExample || '')
          : stringifyExample(exampleValue);
        const refName =
          typeof schema?.$ref === 'string'
            ? resolveRefName(schema.$ref)
            : typeof schema?.originalRef === 'string'
              ? String(schema.originalRef)
              : isPlainStringBody
                ? 'string'
                : 'object';

        parameters.push({
          name: bodyParam.name || 'body',
          in: 'body',
          description: stripHtml(bodyParam.description ?? 'Request body'),
          descriptionHtml,
          required: Boolean(bodyParam.required),
          type: refName,
          multi: false,
          example: exampleText || undefined,
        });
      }

      operations.push({
        id: `${method}:${path}`,
        method,
        path,
        shortPath: shortPath(path),
        tag,
        tagLabel: resolveTagLabel(tag, tagInfo?.description),
        summary: raw.summary ?? path,
        descriptionHtml: sanitizeDescriptionHtml(raw.description ?? ''),
        descriptionText: stripHtml(raw.description ?? raw.summary ?? ''),
        parameters,
        requiresAuth: operationRequiresAuth(raw),
        produces: raw.produces?.length ? raw.produces : ['*/*', 'application/json'],
        consumes: raw.consumes?.length ? raw.consumes : [],
      });
    }
  }

  const groupOrder = (doc.tags ?? []).map(t => t.name);
  const grouped = new Map<string, ConsoleTagGroup>();

  for (const op of operations) {
    if (!grouped.has(op.tag)) {
      const meta = tagMeta.get(op.tag);
      grouped.set(op.tag, {
        name: op.tag,
        label: op.tagLabel,
        description: meta?.description,
        operations: [],
      });
    }
    grouped.get(op.tag)!.operations.push(op);
  }

  const groups = [
    ...groupOrder.filter(name => grouped.has(name)).map(name => grouped.get(name)!),
    ...[...grouped.values()].filter(g => !groupOrder.includes(g.name)),
  ];

  return {
    title: doc.info?.title ?? 'DISGENET API',
    version: doc.info?.version ?? '',
    baseUrl: buildBaseUrl(doc, fallbackBase),
    groups,
    operations,
    models: parseOpenApiModels(doc),
  };
}

const JAVA_RUNTIME_MODELS = new Set(['File', 'InputStream', 'Resource', 'URI', 'URL', 'URLStreamHandler']);

export function formatModelDisplayName(name: string): string {
  return name.replace(/«/g, '<').replace(/»/g, '>');
}

function schemaTypeLabel(schema: SchemaNode): { label: string; ref?: string } {
  if (typeof schema.$ref === 'string') {
    const name = resolveRefName(schema.$ref);
    return { label: formatModelDisplayName(name), ref: name };
  }

  if (schema.type === 'array') {
    const inner = schemaTypeLabel((schema.items ?? {}) as SchemaNode);
    return { label: `array[${inner.label}]`, ref: inner.ref };
  }

  if (schema.additionalProperties && typeof schema.additionalProperties === 'object') {
    const inner = schemaTypeLabel(schema.additionalProperties as SchemaNode);
    return { label: `map<string, ${inner.label}>`, ref: inner.ref };
  }

  const type = String(schema.type ?? 'object');
  const format = typeof schema.format === 'string' ? schema.format : undefined;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return {
      label: `enum (${schema.enum.map(String).slice(0, 6).join(', ')}${schema.enum.length > 6 ? ', …' : ''})`,
    };
  }
  return { label: format ? `${type} (${format})` : type };
}

export function parseOpenApiModels(doc: Swagger2Document): ConsoleModel[] {
  const defs = doc.definitions ?? {};
  const models: ConsoleModel[] = [];

  for (const [name, raw] of Object.entries(defs)) {
    if (JAVA_RUNTIME_MODELS.has(name)) continue;
    const schema = raw as SchemaNode;
    const props = (schema.properties ?? {}) as Record<string, SchemaNode>;
    const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);

    models.push({
      name,
      displayName: formatModelDisplayName(name),
      description: typeof schema.description === 'string' ? schema.description : '',
      type: String(schema.type ?? 'object'),
      properties: Object.entries(props).map(([propName, propSchema]) => {
        const t = schemaTypeLabel(propSchema);
        return {
          name: propName,
          typeLabel: t.label,
          typeRef: t.ref,
          description: typeof propSchema.description === 'string' ? propSchema.description : '',
          required: required.has(propName),
        };
      }),
    });
  }

  return models.sort((a, b) => {
    const aWrap = a.name.startsWith('Response');
    const bWrap = b.name.startsWith('Response');
    if (aWrap !== bWrap) return aWrap ? 1 : -1;
    return a.displayName.localeCompare(b.displayName);
  });
}

export function buildRequestUrl(
  baseUrl: string,
  operation: ConsoleOperation,
  values: Record<string, string>,
): string {
  let path = operation.path;
  const query = new URLSearchParams();

  for (const param of operation.parameters) {
    const raw = values[param.name]?.trim() ?? '';
    if (!raw) continue;

    if (param.in === 'path') {
      path = path.replace(`{${param.name}}`, encodeURIComponent(raw));
      continue;
    }

    if (param.in === 'query') {
      if (param.multi) {
        raw
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
          .forEach(v => query.append(param.name, v));
      } else {
        query.set(param.name, raw);
      }
    }
  }

  const qs = query.toString();
  return `${baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;
}

export function getOpenApiSpecUrl(apiBase = process.env.REACT_APP_URL_API_API ?? ''): string {
  const base = apiBase.replace(/\/$/, '');
  return `${base}/v2/api-docs`;
}

export function initialParamValues(operation: ConsoleOperation): Record<string, string> {
  const preset = OPERATION_PARAM_PRESETS[operation.path];
  const values: Record<string, string> = {};

  for (const param of operation.parameters) {
    if (preset) {
      values[param.name] = preset[param.name] ?? '';
    } else {
      values[param.name] = param.defaultValue ?? param.example ?? '';
    }
  }

  return values;
}
